export interface NoiseLogCandidate {
  message: string;
  http?: { status_code?: number };
}

const NOISE_PATTERNS: RegExp[] = [
  // Benign browser notification on layout-heavy pages (map resize), not actionable.
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i,
  // Opaque browser Event thrown as-is; carries no actionable information.
  /^Uncaught \{"isTrusted":true\}$/,
  // Stale bundle after a deploy: hashed chunks 404 in tabs opened before it.
  // Chrome says "Failed to fetch dynamically imported module", Firefox
  // "error loading dynamically imported module", Safari has its own wording.
  /dynamically imported module/i,
  /Importing a module script failed/i,
  // matomo.js blocked by an ad or tracking blocker in the viewer's browser.
  /\[vue-matomo\] An error occurred trying to load/,
  // Cross-origin script error: the browser hides its message and stack.
  /^Uncaught "Script error\."$/,
  // Raised by a browser extension, not by our code.
  /Invalid call to runtime\.sendMessage\(\)\. Tab not found/,
];

/** Known-benign browser/SDK noise that should not reach Datadog. */
export function isNoiseLog(log: NoiseLogCandidate): boolean {
  if (NOISE_PATTERNS.some(pattern => pattern.test(log.message))) return true;
  return isNetworkTransportFailure(log);
}

/**
 * A fetch/XHR that never completed — dropped mobile connection, DNS/TLS
 * failure, or a request deliberately aborted (MapLibre GL cancels every tile
 * fetch that scrolls out of the viewport during pan/zoom). Browsers surface
 * these as "Failed to fetch" (Chromium) / "Load failed" (WebKit), and the
 * Datadog SDK as "Fetch error GET <url>" / "XHR error GET <url>". They are
 * inherent to mobile networks and retried by the pollers, so they are not
 * actionable.
 *
 * A completed response always carries a status code, so we discriminate on the
 * structured `http.status_code` first: the Datadog SDK emits the same
 * "Fetch error GET <url>" message for a 5xx (status_code >= 500) as for an
 * aborted request (status_code 0), so matching on text alone would drop genuine
 * API outages. Requests thrown by api-helper.ts ("<status>: <text> when trying
 * to get <url>") carry the status in the message instead and are also kept.
 */
function isNetworkTransportFailure({ message, http }: NoiseLogCandidate): boolean {
  // A completed response (even 4xx/5xx) always carries a status code: real signal.
  if (http?.status_code) return false;
  if (/^\d{3}: /.test(message)) return false;
  return /Failed to fetch|Load failed/.test(message) || /^(Fetch|XHR) error /.test(message);
}
